import React, {
    createContext,
    Dispatch,
    FunctionComponent,
    ReactNode,
    useContext,
    useReducer,
    useEffect,
    useRef,
} from 'react';

import { Alert } from 'react-bootstrap';

import 'common/message-bar/MessageBar.scss';

enum MessageType {
    Success,
    Error,
    Warning,
}

export interface Message {
    type?: MessageType;
    text?: string;
    visible: boolean;
}

export enum MessageActionType {
    ShowSuccess,
    ShowError,
    ShowWarning,
    Hide,
}

export interface MessageAction {
    type: MessageActionType;
    text?: string;
}

interface MessageProviderProps {
    children: ReactNode;
}

const MessageStateContext = createContext<Message | undefined>(undefined);
const MessageDispatchContext = createContext<Dispatch<MessageAction> | undefined>(undefined);

const messageReducer = (message: Message, action: MessageAction): Message => {
    switch (action.type) {
        case MessageActionType.ShowSuccess: {
            return { type: MessageType.Success, text: action.text, visible: true };
        }

        case MessageActionType.ShowError: {
            return { type: MessageType.Error, text: action.text, visible: true };
        }

        case MessageActionType.ShowWarning: {
            return { type: MessageType.Warning, text: action.text, visible: true };
        }

        case MessageActionType.Hide: {
            return { visible: false };
        }

        default: {
            throw new Error(`Invalid action type: ${action.type}`);
        }
    }
};

export const MessageProvider: FunctionComponent<MessageProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(messageReducer, { visible: false });

    return (
        <MessageStateContext.Provider value={state}>
            <MessageDispatchContext.Provider value={dispatch}>{children}</MessageDispatchContext.Provider>
        </MessageStateContext.Provider>
    );
};

export const useMessageState = (): Message => {
    const context = useContext(MessageStateContext);

    if (context === undefined) {
        throw new Error('useMessageState must be used within a MessageProvider');
    }

    return context;
};

export const useMessageDispatch = (): Dispatch<MessageAction> => {
    const context = useContext(MessageDispatchContext);

    if (context === undefined) {
        throw new Error('useMessageDispatch must be used within a MessageProvider');
    }

    return context;
};

export const useMessage = (): [Message, Dispatch<MessageAction>] => {
    return [useMessageState(), useMessageDispatch()];
};

export const MessageBar = (): JSX.Element | null => {
    const MESSAGE_DURATION = 10000; // 10s
    const LONG_MESSAGE_DURATION = 60000; // 60s
    const [message, dispatch] = useMessage() as [Message, React.Dispatch<MessageAction>];
    const timeoutRef = useRef<NodeJS.Timeout>();

    const hide = (): void => {
        dispatch({ type: MessageActionType.Hide });
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    let alertComponent;

    if (!message || !message.visible) {
        return null;
    }
    switch (message.type) {
        case MessageType.Success:
            alertComponent = (
                <Alert variant="success">
                    <i className="icon-check-circle" /> <span>{message.text}</span>
                </Alert>
            );

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                hide();
            }, MESSAGE_DURATION);
            break;

        case MessageType.Error:
            alertComponent = (
                <Alert variant="danger" dismissible onClose={hide}>
                    <i className="icon-exclamation-circle" />
                    <span>{message.text}</span>
                </Alert>
            );

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                hide();
            }, LONG_MESSAGE_DURATION);
            break;

        case MessageType.Warning:
            alertComponent = (
                <Alert variant="warning" dismissible onClose={hide}>
                    <i className="icon-exclamation-triangle" />
                    <span>{message.text}</span>
                </Alert>
            );

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                hide();
            }, LONG_MESSAGE_DURATION);
            break;

        default:
            throw new Error(`Invalid message type: ${message.type}`);
    }
    return <div className="r4e-message-bar">{alertComponent}</div>;
};
